import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Badge, Button, ButtonGroup, Card, Col, Form, Modal, OverlayTrigger,
  Row, Spinner, Tab, Table, Tabs, Tooltip,
} from 'react-bootstrap'
import { useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import axios from 'axios'

import { URL as BACKEND_URL, fetchNewToken } from '../../gql/client'
import { STORAGE_NAMES } from '../../helpers/consts'
import { GET_ALL_PIPELINES } from '../../gql/queries/pipelines'
import { GET_ALL_TAGS } from '../../gql/queries/tagsQuery'
import { parseId } from '../../helpers'
import {
  AUTO_PUSH_MESSAGES as M,
  EM_DASH,
  REQUIRED_MARK,
  RESULT_VALUES,
  campaignOption,
  listOption,
  resultLabel,
  vicidialLeadRef,
} from './autoPushMessages'
import { TELEPHONY_MESSAGES as TM } from '../../helpers/telephonyMessages'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AutoPushRule {
  id: number
  name: string
  description: string
  organization_id: number | null
  enabled: boolean
  dry_run: boolean
  priority: number
  target_campaign_id: string
  target_list_id: number | null
  run_every_minutes: number
  batch_size: number
  hopper_target_level: number | null
  time_window_start: string
  time_window_end: string
  timezone: string
  weekdays_only: boolean
  pipeline_id: number | null
  stage_pipeline_ids: number[]
  tags_required_ids: number[]
  tags_excluded_ids: number[]
  lead_source_ids: number[]
  max_age_days: number
  min_age_minutes: number
  state_filter: string[]
  max_call_attempts: number
  cooldown_hours: number
  never_repush_dispos: string[]
  repush_after_dispos: Record<string, number>
  priority_order: 'newest_first' | 'oldest_first'
  last_run_at: string
  last_run_pushed_count: number
  last_run_skipped_count: number
  last_run_error: string
  created: string
  updated: string
}

interface VicidialCampaign {
  campaign_id: string
  campaign_name: string
  active: string
}

interface VicidialList {
  list_id: string
  list_name: string
  campaign_id: string
  active: string
  list_lastcalldate?: string | null
}

interface LeadCampaignPush {
  id: number
  lead_id: number
  lead_name: string
  lead_phone: string
  rule_id: number | null
  rule_name: string
  target_campaign_id: string
  pushed_at: string
  vicidial_lead_id: string
  result: 'success' | 'failure' | 'skipped' | 'dry_run'
  skip_reason: string
  error_detail: string
}

interface RuleStats {
  rule_id: number
  last_24h: { success: number; failure: number; skipped: number; dry_run: number }
  last_7d: { success: number; failure: number; skipped: number; dry_run: number }
  last_30d: { success: number; failure: number; skipped: number; dry_run: number }
  last_run_at: string
  last_run_error: string
}

// ---------------------------------------------------------------------------
// Auth helpers (same pattern as VicidialCampaignsPage)
// ---------------------------------------------------------------------------

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

async function authFetch<T>(method: 'get' | 'post' | 'patch' | 'delete' | 'put', url: string, data?: any): Promise<T> {
  try {
    const resp = await axios.request<T>({ method, url, headers: authHeaders(), data })
    return resp.data
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        const resp = await axios.request<T>({
          method, url, data,
          headers: { Authorization: `Bearer ${newToken}` },
        })
        return resp.data
      }
    }
    throw err
  }
}

const formatDateTime = (iso: string) => (iso ? new Date(iso).toLocaleString() : EM_DASH)

const RESULT_VARIANTS: Record<string, string> = {
  success: 'success',
  failure: 'danger',
  skipped: 'secondary',
  dry_run: 'info',
}

// ---------------------------------------------------------------------------
// Edit modal (inline)
// ---------------------------------------------------------------------------

interface EditModalProps {
  show: boolean
  rule: AutoPushRule | null
  campaigns: VicidialCampaign[]
  pipelines: any[]
  tags: any[]
  onClose: () => void
  onSaved: () => void
}

const EMPTY_RULE: AutoPushRule = {
  id: 0,
  name: '',
  description: '',
  organization_id: null,
  enabled: false,
  dry_run: true,
  priority: 100,
  target_campaign_id: '',
  target_list_id: null,
  run_every_minutes: 15,
  batch_size: 50,
  hopper_target_level: null,
  time_window_start: '09:00',
  time_window_end: '19:00',
  timezone: 'America/New_York',
  weekdays_only: true,
  pipeline_id: null,
  stage_pipeline_ids: [],
  tags_required_ids: [],
  tags_excluded_ids: [],
  lead_source_ids: [],
  max_age_days: 60,
  min_age_minutes: 5,
  state_filter: [],
  max_call_attempts: 5,
  cooldown_hours: 24,
  never_repush_dispos: ['SALE', 'DISA', 'DNCL', 'DC'],
  repush_after_dispos: { NANS: 12, VMAIL: 6 },
  priority_order: 'newest_first',
  last_run_at: '',
  last_run_pushed_count: 0,
  last_run_skipped_count: 0,
  last_run_error: '',
  created: '',
  updated: '',
}

const EditModal: React.FC<EditModalProps> = ({ show, rule, campaigns, pipelines, tags, onClose, onSaved }) => {
  const intl = useIntl()
  const [form, setForm] = useState<AutoPushRule>(EMPTY_RULE)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [stateFilterStr, setStateFilterStr] = useState('')
  const [neverRepushStr, setNeverRepushStr] = useState('')
  const [repushAfterStr, setRepushAfterStr] = useState('')
  const [lists, setLists] = useState<VicidialList[]>([])
  const [loadingLists, setLoadingLists] = useState(false)

  useEffect(() => {
    if (show) {
      const initial = rule ? { ...rule } : { ...EMPTY_RULE }
      setForm(initial)
      setStateFilterStr((initial.state_filter || []).join(', '))
      setNeverRepushStr((initial.never_repush_dispos || []).join(', '))
      setRepushAfterStr(
        Object.entries(initial.repush_after_dispos || {})
          .map(([k, v]) => `${k}:${v}`)
          .join(', ')
      )
      setError('')
    }
  }, [show, rule])

  // Fetch the VICIdial lists for the currently selected campaign so the
  // List dropdown stays in sync. If only one list matches the campaign,
  // auto-select it.
  useEffect(() => {
    if (!show || !form.target_campaign_id) {
      setLists([])
      return
    }
    let cancelled = false
    setLoadingLists(true)
    authFetch<{ lists: VicidialList[] }>(
      'get',
      `${BACKEND_URL}/api/telephony/vicidial/lists?campaign_id=${encodeURIComponent(form.target_campaign_id)}`,
    )
      .then((data) => {
        if (cancelled) return
        const fetched = data.lists || []
        setLists(fetched)
        // Auto-select when there's exactly one option and the rule has no list yet
        if (fetched.length === 1 && !form.target_list_id) {
          const onlyId = Number(fetched[0].list_id)
          if (!Number.isNaN(onlyId)) {
            setForm((prev) => ({ ...prev, target_list_id: onlyId }))
          }
        }
        // Clear stale list_id if it no longer belongs to the selected campaign
        if (
          form.target_list_id &&
          !fetched.some((l) => Number(l.list_id) === form.target_list_id)
        ) {
          setForm((prev) => ({ ...prev, target_list_id: null }))
        }
      })
      .catch(() => {
        if (!cancelled) setLists([])
      })
      .finally(() => {
        if (!cancelled) setLoadingLists(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, form.target_campaign_id])

  const stagesForPipeline = useMemo(() => {
    if (!form.pipeline_id) return []
    const pipeline = pipelines.find(({ node }: any) => Number(parseId(node.id)) === form.pipeline_id)
    return pipeline?.node?.stagePipelineSet?.edges || []
  }, [form.pipeline_id, pipelines])

  const update = (patch: Partial<AutoPushRule>) => setForm((prev) => ({ ...prev, ...patch }))

  const toggleArrayValue = (arr: number[], val: number) =>
    arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]

  const save = async () => {
    if (!form.name.trim()) {
      setError(intl.formatMessage(M.errorNameRequired))
      return
    }
    if (!form.target_campaign_id.trim()) {
      setError(intl.formatMessage(M.errorCampaignRequired))
      return
    }
    if (lists.length > 0 && !form.target_list_id) {
      setError(intl.formatMessage(M.errorListRequired))
      return
    }

    // Parse free-text fields
    const states = stateFilterStr.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)
    const neverRepush = neverRepushStr.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)
    const repushAfter: Record<string, number> = {}
    repushAfterStr.split(',').forEach((pair) => {
      const [k, v] = pair.split(':').map((x) => x.trim())
      if (k && v && !Number.isNaN(Number(v))) repushAfter[k.toUpperCase()] = Number(v)
    })

    const payload = {
      ...form,
      state_filter: states,
      never_repush_dispos: neverRepush,
      repush_after_dispos: repushAfter,
    }

    setSaving(true)
    setError('')
    try {
      const url = rule
        ? `${BACKEND_URL}/api/telephony/auto-push/rules/${rule.id}`
        : `${BACKEND_URL}/api/telephony/auto-push/rules`
      const method = rule ? 'patch' : 'post'
      await authFetch(method, url, payload)
      toast.success(intl.formatMessage(rule ? M.toastRuleUpdated : M.toastRuleCreated))
      onSaved()
      onClose()
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || intl.formatMessage(M.errorSaveFailed)
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} size='xl' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>
          {rule
            ? intl.formatMessage(M.modalEditTitle, { name: rule.name })
            : intl.formatMessage(M.modalNewTitle)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}

        <Tabs defaultActiveKey='identity' className='mb-3'>
          <Tab eventKey='identity' title={intl.formatMessage(M.tabIdentity)}>
            <Row className='g-3'>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>
                    {intl.formatMessage(M.name)} <span className='text-danger'>{REQUIRED_MARK}</span>
                  </Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(e) => update({ name: e.target.value })}
                    maxLength={120}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldPriority)}</Form.Label>
                  <Form.Control
                    type='number'
                    value={form.priority}
                    onChange={(e) => update({ priority: Number(e.target.value) })}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.description)}</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={2}
                    value={form.description}
                    onChange={(e) => update({ description: e.target.value })}
                    maxLength={512}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Check
                  type='switch'
                  id='rule-enabled'
                  label={intl.formatMessage(M.fieldEnabled)}
                  checked={form.enabled}
                  onChange={(e) => update({ enabled: e.target.checked })}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type='switch'
                  id='rule-dryrun'
                  label={intl.formatMessage(M.fieldDryRun)}
                  checked={form.dry_run}
                  onChange={(e) => update({ dry_run: e.target.checked })}
                />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey='target' title={intl.formatMessage(M.tabTarget)}>
            <Row className='g-3'>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {intl.formatMessage(M.fieldTargetCampaign)}
                    <span className='text-danger ms-1'>{REQUIRED_MARK}</span>
                  </Form.Label>
                  <Form.Select
                    value={form.target_campaign_id}
                    onChange={(e) => update({ target_campaign_id: e.target.value })}
                  >
                    <option value=''>{intl.formatMessage(M.selectPlaceholder)}</option>
                    {campaigns.map((c) => (
                      <option key={c.campaign_id} value={c.campaign_id}>
                        {campaignOption(c.campaign_id, c.campaign_name)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text muted>{intl.formatMessage(M.campaignMustExist)}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {intl.formatMessage(M.fieldList)}
                    <span className='text-danger ms-1'>{REQUIRED_MARK}</span>
                  </Form.Label>
                  <Form.Select
                    value={form.target_list_id ?? ''}
                    onChange={(e) =>
                      update({ target_list_id: e.target.value ? Number(e.target.value) : null })
                    }
                    disabled={!form.target_campaign_id || loadingLists}
                  >
                    {!form.target_campaign_id && (
                      <option value=''>{intl.formatMessage(M.selectCampaignFirst)}</option>
                    )}
                    {form.target_campaign_id && loadingLists && (
                      <option value=''>{intl.formatMessage(M.loadingLists)}</option>
                    )}
                    {form.target_campaign_id && !loadingLists && lists.length === 0 && (
                      <option value=''>{intl.formatMessage(M.noListsForCampaign)}</option>
                    )}
                    {form.target_campaign_id && !loadingLists && lists.length > 0 && (
                      <>
                        <option value=''>{intl.formatMessage(M.selectPlaceholder)}</option>
                        {lists.map((l) => (
                          <option key={l.list_id} value={l.list_id}>
                            {listOption(l.list_id, l.list_name)}
                          </option>
                        ))}
                      </>
                    )}
                  </Form.Select>
                  {form.target_campaign_id && !loadingLists && lists.length === 0 && (
                    <Form.Text className='text-danger'>
                      {intl.formatMessage(M.noListsHint)}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey='schedule' title={intl.formatMessage(M.tabSchedule)}>
            <Row className='g-3'>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldRunEvery)}</Form.Label>
                  <Form.Control
                    type='number'
                    min={1}
                    value={form.run_every_minutes}
                    onChange={(e) => update({ run_every_minutes: Math.max(1, Number(e.target.value)) })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldBatchSize)}</Form.Label>
                  <Form.Control
                    type='number'
                    min={1}
                    value={form.batch_size}
                    onChange={(e) => update({ batch_size: Math.max(1, Number(e.target.value)) })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldHopperTarget)}</Form.Label>
                  <Form.Control
                    type='number'
                    value={form.hopper_target_level ?? ''}
                    onChange={(e) => update({ hopper_target_level: e.target.value ? Number(e.target.value) : null })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldWindowStart)}</Form.Label>
                  <Form.Control
                    type='time'
                    value={form.time_window_start || ''}
                    onChange={(e) => update({ time_window_start: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldWindowEnd)}</Form.Label>
                  <Form.Control
                    type='time'
                    value={form.time_window_end || ''}
                    onChange={(e) => update({ time_window_end: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldTimezone)}</Form.Label>
                  <Form.Control
                    value={form.timezone}
                    onChange={(e) => update({ timezone: e.target.value })}
                    placeholder='America/New_York'
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Check
                  type='switch'
                  id='rule-weekdays'
                  label={intl.formatMessage(M.fieldWeekdaysOnly)}
                  checked={form.weekdays_only}
                  onChange={(e) => update({ weekdays_only: e.target.checked })}
                />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey='filters' title={intl.formatMessage(M.tabFilters)}>
            <Row className='g-3'>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldPipeline)}</Form.Label>
                  <Form.Select
                    value={form.pipeline_id ?? ''}
                    onChange={(e) => {
                      const newPipelineId = e.target.value ? Number(e.target.value) : null
                      update({ pipeline_id: newPipelineId, stage_pipeline_ids: [] })
                    }}
                  >
                    <option value=''>{intl.formatMessage(M.anyPipeline)}</option>
                    {pipelines.map(({ node }: any) => (
                      <option key={node.id} value={Number(parseId(node.id))}>
                        {node.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldStages)}</Form.Label>
                  <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4, padding: 8 }}>
                    {stagesForPipeline.length === 0 && (
                      <div className='text-muted small'>
                        {intl.formatMessage(M.pickPipelineFirst)}
                      </div>
                    )}
                    {stagesForPipeline.map(({ node }: any) => {
                      const stageId = Number(parseId(node.id))
                      const checked = form.stage_pipeline_ids.includes(stageId)
                      return (
                        <Form.Check
                          key={node.id}
                          type='checkbox'
                          id={`stage-${node.id}`}
                          label={node.name}
                          checked={checked}
                          onChange={() => update({ stage_pipeline_ids: toggleArrayValue(form.stage_pipeline_ids, stageId) })}
                        />
                      )
                    })}
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldMaxAgeDays)}</Form.Label>
                  <Form.Control
                    type='number'
                    min={1}
                    value={form.max_age_days}
                    onChange={(e) => update({ max_age_days: Math.max(0, Number(e.target.value)) })}
                  />
                  <Form.Text muted>{intl.formatMessage(M.maxAgeDaysHint)}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldMinAgeMinutes)}</Form.Label>
                  <Form.Control
                    type='number'
                    min={0}
                    value={form.min_age_minutes}
                    onChange={(e) => update({ min_age_minutes: Math.max(0, Number(e.target.value)) })}
                  />
                  <Form.Text muted>{intl.formatMessage(M.minAgeMinutesHint)}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldTagsRequired)}</Form.Label>
                  <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4, padding: 8 }}>
                    {tags.map(({ node }: any) => {
                      const tagId = Number(parseId(node.id))
                      const checked = form.tags_required_ids.includes(tagId)
                      return (
                        <Form.Check
                          key={`req-${node.id}`}
                          type='checkbox'
                          id={`tagreq-${node.id}`}
                          label={node.name}
                          checked={checked}
                          onChange={() => update({ tags_required_ids: toggleArrayValue(form.tags_required_ids, tagId) })}
                        />
                      )
                    })}
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldTagsExcluded)}</Form.Label>
                  <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4, padding: 8 }}>
                    {tags.map(({ node }: any) => {
                      const tagId = Number(parseId(node.id))
                      const checked = form.tags_excluded_ids.includes(tagId)
                      return (
                        <Form.Check
                          key={`exc-${node.id}`}
                          type='checkbox'
                          id={`tagexc-${node.id}`}
                          label={node.name}
                          checked={checked}
                          onChange={() => update({ tags_excluded_ids: toggleArrayValue(form.tags_excluded_ids, tagId) })}
                        />
                      )
                    })}
                  </div>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldStateFilter)}</Form.Label>
                  <Form.Control
                    value={stateFilterStr}
                    onChange={(e) => setStateFilterStr(e.target.value)}
                    placeholder='GA, FL, NC'
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldLeadOrdering)}</Form.Label>
                  <Form.Select
                    value={form.priority_order}
                    onChange={(e) => update({ priority_order: e.target.value as any })}
                  >
                    <option value='newest_first'>{intl.formatMessage(M.newestFirst)}</option>
                    <option value='oldest_first'>{intl.formatMessage(M.oldestFirst)}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey='antispam' title={intl.formatMessage(M.tabAntispam)}>
            <Row className='g-3'>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldMaxAttempts)}</Form.Label>
                  <Form.Control
                    type='number'
                    min={1}
                    value={form.max_call_attempts}
                    onChange={(e) => update({ max_call_attempts: Math.max(1, Number(e.target.value)) })}
                  />
                  <Form.Text muted>{intl.formatMessage(M.maxAttemptsHint)}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldCooldown)}</Form.Label>
                  <Form.Control
                    type='number'
                    min={0}
                    value={form.cooldown_hours}
                    onChange={(e) => update({ cooldown_hours: Math.max(0, Number(e.target.value)) })}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldNeverRepush)}</Form.Label>
                  <Form.Control
                    value={neverRepushStr}
                    onChange={(e) => setNeverRepushStr(e.target.value)}
                    placeholder='SALE, DISA, DNCL, DC'
                  />
                  <Form.Text muted>{intl.formatMessage(M.neverRepushHint)}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.fieldRepushAfter)}</Form.Label>
                  <Form.Control
                    value={repushAfterStr}
                    onChange={(e) => setRepushAfterStr(e.target.value)}
                    placeholder='NANS:12, VMAIL:6, HUPNR:4'
                  />
                  <Form.Text muted>{intl.formatMessage(M.repushAfterHint)}</Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={saving}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button variant='primary' onClick={save} disabled={saving}>
          {saving ? (
            <Spinner size='sm' animation='border' />
          ) : (
            intl.formatMessage(rule ? M.saveChanges : M.createRule)
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Audit log section
// ---------------------------------------------------------------------------

const AuditLog: React.FC<{ rules: AutoPushRule[] }> = ({ rules }) => {
  const intl = useIntl()
  const [pushes, setPushes] = useState<LeadCampaignPush[]>([])
  const [loading, setLoading] = useState(false)
  const [filterRuleId, setFilterRuleId] = useState('')
  const [filterResult, setFilterResult] = useState('')
  const [sinceHours, setSinceHours] = useState(24)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filterRuleId) params.set('rule_id', filterRuleId)
      if (filterResult) params.set('result', filterResult)
      if (sinceHours) params.set('since_hours', String(sinceHours))
      params.set('limit', '200')
      const data = await authFetch<{ pushes: LeadCampaignPush[] }>(
        'get',
        `${BACKEND_URL}/api/telephony/auto-push/pushes?${params.toString()}`,
      )
      setPushes(data.pushes || [])
    } catch (err: any) {
      toast.error(err?.response?.data?.error || intl.formatMessage(M.auditLoadFailed))
    } finally {
      setLoading(false)
    }
  }, [intl, filterRuleId, filterResult, sinceHours])

  useEffect(() => { load() }, [load])

  return (
    <div>
      <Row className='g-2 mb-3 align-items-end'>
        <Col md={3}>
          <Form.Label className='small text-muted mb-1'>
            {intl.formatMessage(M.auditRule)}
          </Form.Label>
          <Form.Select size='sm' value={filterRuleId} onChange={(e) => setFilterRuleId(e.target.value)}>
            <option value=''>{intl.formatMessage(M.auditAllRules)}</option>
            {rules.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className='small text-muted mb-1'>
            {intl.formatMessage(M.auditResult)}
          </Form.Label>
          <Form.Select size='sm' value={filterResult} onChange={(e) => setFilterResult(e.target.value)}>
            <option value=''>{intl.formatMessage(M.auditAllResults)}</option>
            {RESULT_VALUES.map((value) => (
              <option key={value} value={value}>
                {resultLabel(intl, value)}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className='small text-muted mb-1'>
            {intl.formatMessage(M.auditSince)}
          </Form.Label>
          <Form.Select size='sm' value={sinceHours} onChange={(e) => setSinceHours(Number(e.target.value))}>
            <option value={1}>{intl.formatMessage(M.auditLastHour)}</option>
            <option value={24}>{intl.formatMessage(M.auditLast24h)}</option>
            <option value={168}>{intl.formatMessage(M.auditLast7d)}</option>
            <option value={720}>{intl.formatMessage(M.auditLast30d)}</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button size='sm' onClick={load} disabled={loading}>
            {loading ? (
              <Spinner size='sm' animation='border' />
            ) : (
              intl.formatMessage(M.refresh)
            )}
          </Button>
        </Col>
      </Row>

      <Table size='sm' hover responsive>
        <thead className='table-light'>
          <tr>
            <th>{intl.formatMessage(M.auditColWhen)}</th>
            <th>{intl.formatMessage(M.auditRule)}</th>
            <th>{intl.formatMessage(M.auditColLead)}</th>
            <th>{intl.formatMessage(M.phone)}</th>
            <th>{intl.formatMessage(TM.campaign)}</th>
            <th>{intl.formatMessage(M.auditResult)}</th>
            <th>{intl.formatMessage(M.auditColDetail)}</th>
          </tr>
        </thead>
        <tbody>
          {pushes.length === 0 && (
            <tr>
              <td colSpan={7} className='text-center text-muted py-3'>
                {intl.formatMessage(M.auditEmpty)}
              </td>
            </tr>
          )}
          {pushes.map((p) => (
            <tr key={p.id}>
              <td className='text-nowrap'>{formatDateTime(p.pushed_at)}</td>
              <td>
                {p.rule_name || (
                  <em className='text-muted'>{intl.formatMessage(M.auditManual)}</em>
                )}
              </td>
              <td>{p.lead_name}</td>
              <td className='text-nowrap'>{p.lead_phone}</td>
              <td>{p.target_campaign_id}</td>
              <td>
                <Badge bg={RESULT_VARIANTS[p.result] || 'secondary'}>
                  {resultLabel(intl, p.result)}
                </Badge>
              </td>
              <td className='small text-muted'>
                {p.skip_reason || (p.vicidial_lead_id ? vicidialLeadRef(p.vicidial_lead_id) : '')}
                {p.error_detail && (
                  <OverlayTrigger overlay={<Tooltip>{p.error_detail}</Tooltip>}>
                    <i className='bi bi-info-circle ms-1' />
                  </OverlayTrigger>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const AutoPushRulesPage = () => {
  const intl = useIntl()
  const [rules, setRules] = useState<AutoPushRule[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [campaigns, setCampaigns] = useState<VicidialCampaign[]>([])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingRule, setEditingRule] = useState<AutoPushRule | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const { data: pipelinesData } = useQuery(GET_ALL_PIPELINES, { fetchPolicy: 'cache-first' })
  const { data: tagsData } = useQuery(GET_ALL_TAGS, { fetchPolicy: 'cache-first' })
  const pipelines = pipelinesData?.allPipelines?.edges || []
  const tags = tagsData?.allTagInsurances?.edges || tagsData?.tagInsurances?.edges || []

  const loadRules = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await authFetch<{ rules: AutoPushRule[] }>(
        'get', `${BACKEND_URL}/api/telephony/auto-push/rules`,
      )
      setRules(data.rules || [])
    } catch (err: any) {
      setError(err?.response?.data?.error || intl.formatMessage(M.loadRulesFailed))
    } finally {
      setLoading(false)
    }
  }, [intl])

  const loadCampaigns = useCallback(async () => {
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/telephony/vicidial/campaigns`,
        {},
        { headers: authHeaders() },
      )
      setCampaigns(resp.data?.campaigns || [])
    } catch {
      // silent — campaigns are needed only for dropdown
    }
  }, [])

  useEffect(() => {
    loadRules()
    loadCampaigns()
  }, [loadRules, loadCampaigns])

  const toggleEnabled = async (rule: AutoPushRule) => {
    setBusyId(rule.id)
    try {
      await authFetch('patch', `${BACKEND_URL}/api/telephony/auto-push/rules/${rule.id}`, {
        enabled: !rule.enabled,
      })
      toast.success(
        intl.formatMessage(!rule.enabled ? M.toastRuleEnabled : M.toastRuleDisabled)
      )
      loadRules()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || intl.formatMessage(M.toastToggleFailed))
    } finally {
      setBusyId(null)
    }
  }

  const runNow = async (rule: AutoPushRule) => {
    if (!window.confirm(intl.formatMessage(M.confirmRun, { name: rule.name }))) return
    setBusyId(rule.id)
    try {
      const data = await authFetch<any>(
        'post', `${BACKEND_URL}/api/telephony/auto-push/rules/${rule.id}/run`, {},
      )
      const pushed = data.pushed ?? 0
      const dryRun = data.dry_run ?? 0
      const skipped = data.skipped ?? 0
      toast.success(
        intl.formatMessage(M.toastRunDone, {
          pushed: String(pushed),
          dryRun: String(dryRun),
          skipped: String(skipped),
        })
      )
      loadRules()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || intl.formatMessage(M.toastRunFailed))
    } finally {
      setBusyId(null)
    }
  }

  const deleteRule = async (rule: AutoPushRule) => {
    if (!window.confirm(intl.formatMessage(M.confirmDelete, { name: rule.name }))) return
    setBusyId(rule.id)
    try {
      await authFetch('delete', `${BACKEND_URL}/api/telephony/auto-push/rules/${rule.id}`)
      toast.success(intl.formatMessage(M.toastRuleDeleted))
      loadRules()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || intl.formatMessage(M.toastDeleteFailed))
    } finally {
      setBusyId(null)
    }
  }

  const openCreate = () => {
    setEditingRule(null)
    setShowEditModal(true)
  }

  const openEdit = (rule: AutoPushRule) => {
    setEditingRule(rule)
    setShowEditModal(true)
  }

  return (
    <div className='container-fluid py-3'>
      <Card className='shadow-sm mb-3'>
        <Card.Header className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='fw-semibold fs-5'>{intl.formatMessage(M.title)}</div>
            <div className='text-muted small'>{intl.formatMessage(M.subtitle)}</div>
          </div>
          <div className='d-flex gap-2'>
            <Button variant='outline-secondary' size='sm' onClick={loadRules} disabled={loading}>
              {loading ? (
                <Spinner size='sm' animation='border' />
              ) : (
                <>
                  <i className='bi bi-arrow-clockwise me-1' />
                  {intl.formatMessage(M.refresh)}
                </>
              )}
            </Button>
            <Button variant='primary' size='sm' onClick={openCreate}>
              <i className='bi bi-plus-lg me-1' />
              {intl.formatMessage(M.newRule)}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant='danger'>{error}</Alert>}

          <Tabs defaultActiveKey='rules' className='mb-3'>
            <Tab eventKey='rules' title={intl.formatMessage(M.tabRules, { count: rules.length })}>
              <Table hover responsive>
                <thead className='table-light'>
                  <tr>
                    <th style={{ width: 80 }}>{intl.formatMessage(M.colEnabled)}</th>
                    <th>{intl.formatMessage(M.name)}</th>
                    <th>{intl.formatMessage(M.colTarget)}</th>
                    <th className='text-end'>{intl.formatMessage(M.colPriority)}</th>
                    <th className='text-end'>{intl.formatMessage(M.colEvery)}</th>
                    <th className='text-end'>{intl.formatMessage(M.colBatch)}</th>
                    <th>{intl.formatMessage(M.colWindow)}</th>
                    <th>{intl.formatMessage(M.colLastRun)}</th>
                    <th className='text-end'>{intl.formatMessage(M.colLastPush)}</th>
                    <th style={{ width: 220 }}>{intl.formatMessage(M.actions)}</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.length === 0 && !loading && (
                    <tr>
                      <td colSpan={10} className='text-center text-muted py-4'>
                        <FormattedMessage
                          {...M.emptyRules}
                          values={{ b: (chunks) => <strong>{chunks}</strong> }}
                        />
                      </td>
                    </tr>
                  )}
                  {rules.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Form.Check
                          type='switch'
                          id={`enable-${r.id}`}
                          checked={r.enabled}
                          disabled={busyId === r.id}
                          onChange={() => toggleEnabled(r)}
                        />
                      </td>
                      <td>
                        <div className='fw-semibold'>{r.name}</div>
                        {r.dry_run && (
                          <Badge bg='info' className='me-1'>
                            {intl.formatMessage(M.dryRunBadge)}
                          </Badge>
                        )}
                        {r.description && <div className='text-muted small'>{r.description}</div>}
                      </td>
                      <td>
                        <Badge bg='primary' text='white'>{r.target_campaign_id}</Badge>
                        {r.target_list_id && (
                          <span className='text-muted small ms-1'>
                            {intl.formatMessage(M.listRef, { id: String(r.target_list_id) })}
                          </span>
                        )}
                      </td>
                      <td className='text-end'>{r.priority}</td>
                      <td className='text-end'>
                        {intl.formatMessage(M.everyMinutes, {
                          minutes: String(r.run_every_minutes),
                        })}
                      </td>
                      <td className='text-end'>{r.batch_size}</td>
                      <td>
                        {intl.formatMessage(M.windowRange, {
                          start: r.time_window_start || EM_DASH,
                          end: r.time_window_end || EM_DASH,
                        })}
                        {r.weekdays_only && (
                          <Badge bg='light' text='dark' className='ms-1 border'>
                            {intl.formatMessage(M.weekdaysBadge)}
                          </Badge>
                        )}
                      </td>
                      <td className='text-nowrap small'>
                        {formatDateTime(r.last_run_at)}
                        {r.last_run_error && (
                          <OverlayTrigger overlay={<Tooltip>{r.last_run_error}</Tooltip>}>
                            <i className='bi bi-exclamation-triangle-fill text-danger ms-1' />
                          </OverlayTrigger>
                        )}
                      </td>
                      <td className='text-end'>{r.last_run_pushed_count}</td>
                      <td>
                        <ButtonGroup size='sm'>
                          <Button variant='outline-primary' onClick={() => runNow(r)} disabled={busyId === r.id}>
                            {busyId === r.id ? (
                              <Spinner size='sm' animation='border' />
                            ) : (
                              <>
                                <i className='bi bi-play-fill' /> {intl.formatMessage(M.run)}
                              </>
                            )}
                          </Button>
                          <Button variant='outline-secondary' onClick={() => openEdit(r)} disabled={busyId === r.id}>
                            <i className='bi bi-pencil' /> {intl.formatMessage(M.edit)}
                          </Button>
                          <Button variant='outline-danger' onClick={() => deleteRule(r)} disabled={busyId === r.id}>
                            <i className='bi bi-trash' />
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>
            <Tab eventKey='audit' title={intl.formatMessage(M.tabAudit)}>
              <AuditLog rules={rules} />
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <EditModal
        show={showEditModal}
        rule={editingRule}
        campaigns={campaigns}
        pipelines={pipelines}
        tags={tags}
        onClose={() => setShowEditModal(false)}
        onSaved={loadRules}
      />
    </div>
  )
}

export default AutoPushRulesPage
